import {exam_createExaminationPaper_Api, exam_getExaminationById_Api,} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {getResponseItem, LoadingWrapper, NullAsDefault} from 'views/Shared'
import {gPaths} from '../configs'
import ExaminationEntity from '../Examination/ExaminationEntity'
import {notifyOnError} from '../factory/createErrorEvent'
import {bindQueryParam, mapPathParam} from '../functions/routerHelper'
import {PermissionEntity} from '../Permissions/PermissionEntity'
import {examFilterActions} from './ExaminationPaperContext'
import {getRouteSearchParams} from "../../../envs/_current/history";
import {setSessionToken} from "../../../helpers/localStorage";

const ExaminationContext = React.createContext({})

export const getLinkToExamination = (examination_id) =>
    ['/examination', examination_id].join('/')

export const useExaminationCreator = ({onSuccess = NullAsDefault, ...props} = {}) => {
    const t = useTranslate()
    const history = useHistory()
    const location = useLocation()
    const asyncData = useAsyncAction({
        apiInfo: exam_createExaminationPaper_Api,
        onError: notifyOnError(t),
        onSuccess: (result, data) => {
            onSuccess(result, data)

            const dataItem = _.get(data, 'response.data')
            if (result) {
                if (dataItem?.session_token) {
                    setSessionToken(
                        dataItem?.session_token
                    )
                }

                let params = getRouteSearchParams()
                params.set(examFilterActions.question, 0)
                history.push({
                    pathname: '/examination-paper/' + result,
                    search: '?' + params.toString(),
                })
            }
        },
        ...props,
    })
    return asyncData
}

export const ExaminationProvider = ({
                                        item,
                                        children,
                                        routeEntities = [],
                                        routePathField = 'path',
                                        ...props
                                    }) => {
    const t = useTranslate()

    const formRef = useRef()

    const apiInfo = exam_getExaminationById_Api

    const id = getId(item) || item

    const cacheId = mapPathParam(apiInfo.path, {id})

    const {response, isLoading = false} = useAsync({
        apiInfo,
        query: bindQueryParam({id}),
    })

    const restSubmitting = () => {
        if (formRef?.current) {
            const {setSubmitting = NullAsDefault} = formRef.current
            setSubmitting(false)
        }
    }

    const {isLoading: isCreating = false, handleAsyncAction: start} =
        useExaminationCreator({
            query: bindQueryParam({id}),
            onError: (e, o) => {
                notifyOnError(t)(e, o)
                restSubmitting()
            },
            onSuccess: restSubmitting,
        })

    const isSucceed = Number(_.get(response, 'status')) === 200

    const cache = getResponseItem(response)

    const error = getResponseItem(response, 'error')

    const errorMessage = _.get(response, 'errorMessages.error')

    const error_code = _.get(response, 'errorMessages.error_code')

    const status = {
        title: getResponseItem(response, 'status'),
        description: errorMessage,
    }

    const defaultRoute = _.first(routeEntities)

    const getDefaultAction = (current) => {
        switch (current) {
            case gPaths.examination:
                return _.get(defaultRoute, routePathField)
            default:
                return null
        }
    }

    const isValidRoute = useCallback(
        (route) => {
            return routeEntities
                .map((item) => _.get(item, routePathField))
                .includes(route)
        },
        [routeEntities, routePathField]
    )

    const renderHeader = useCallback((action, isModal) => null, [])

    return (
        <LoadingWrapper isLoading={!item}>
            <ExaminationEntity
                key={cacheId}
                item={id}>
                {(examination) => (
                    <PermissionEntity item={examination}>
                        <ExaminationContext.Provider
                            value={{
                                id,
                                start,
                                formRef,
                                isLoading,
                                isCreating,
                                examination,
                                error,
                                cache,
                                status,
                                isSucceed,
                                error_code,
                                renderHeader,
                                errorMessage,
                                defaultRoute,
                                routeEntities,
                                routePathField,
                                isValidRoute,
                                getDefaultAction,
                                ...props,
                            }}>
                            {children}
                        </ExaminationContext.Provider>
                    </PermissionEntity>
                )}
            </ExaminationEntity>
        </LoadingWrapper>
    )
}

export default ExaminationContext
